import { useCallback, useEffect, useRef, useState } from "react";
import type { AsyncStatus } from "../types";

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

type UseCommandTimerOptions = {
  /** Timeout in milliseconds before the command is marked failed. Defaults to 5 minutes. */
  timeoutMs?: number;
  /** Called when the command times out. */
  onTimeout?: () => void;
};

type UseCommandTimerReturn = {
  /** Current command status: idle | pending | success | failed */
  commandStatus: AsyncStatus;
  /** Set the command status directly. */
  setCommandStatus: (status: AsyncStatus) => void;
  /** Set status to pending and start the timeout timer. */
  startCommandTimer: () => void;
  /** Cancel the timeout and reset status to idle. */
  clearCommandTimer: () => void;
};

/**
 * Manages the async lifecycle of a device command: pending → success/failed → idle.
 *
 * Handles the 5-minute timeout automatically. The caller is responsible for
 * calling `clearCommandTimer` when the socket ACK arrives.
 *
 * @example
 * const { commandStatus, setCommandStatus, startCommandTimer, clearCommandTimer } = useCommandTimer();
 *
 * const handleGetConfig = async () => {
 *   setCommandStatus("pending");
 *   startCommandTimer();
 *   try {
 *     await api.sendCommand(...);
 *   } catch {
 *     clearCommandTimer();
 *     setCommandStatus("failed");
 *   }
 * };
 *
 * // On socket ACK:
 * // clearCommandTimer();
 * // setCommandStatus("success");
 */
export function useCommandTimer(
  options: UseCommandTimerOptions = {},
): UseCommandTimerReturn {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, onTimeout } = options;
  const [commandStatus, setCommandStatus] = useState<AsyncStatus>("idle");
  const timeoutRef = useRef<number | null>(null);

  const clearCommandTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startCommandTimer = useCallback(() => {
    clearCommandTimer();
    const timer = window.setTimeout(() => {
      setCommandStatus("failed");
      onTimeout?.();
    }, timeoutMs);
    timeoutRef.current = timer;
  }, [clearCommandTimer, timeoutMs, onTimeout]);

  // Auto-reset status to idle after 5 seconds on success/failure
  useEffect(() => {
    if (commandStatus === "success" || commandStatus === "failed") {
      const timer = setTimeout(() => {
        setCommandStatus("idle");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [commandStatus]);

  // Cleanup on unmount
  useEffect(() => () => clearCommandTimer(), [clearCommandTimer]);

  return {
    commandStatus,
    setCommandStatus,
    startCommandTimer,
    clearCommandTimer,
  };
}
